import { useState } from "react";
import { Divider, Stack, useTheme } from "@mui/material";
import { FoodsIcon, FoodsTextInput, Typography } from "@foodpilot/foods";
import { FacetDimensionRow } from "./FacetDimensionRow";
import { filterByLabel } from "./facetLabelFilter";
import { toggleSetMembership } from "./toggleSetMembership";
import type { FacetPanelProps } from "./types";

export const FacetFilterPanel = (props: FacetPanelProps) => {
    const { dimensions, onSelectionChange, labels, isCollapsible = true } = props;
    const theme = useTheme();

    const [searchQuery, setSearchQuery] = useState("");
    const [collapsedDimensionIds, setCollapsedDimensionIds] = useState<Set<string>>(new Set());
    const [expandedValueDimensionIds, setExpandedValueDimensionIds] = useState<Set<string>>(new Set());
    const [valueFilterQueries, setValueFilterQueries] = useState<Record<string, string>>({});

    const populatedDimensions = dimensions.filter((dimension) => dimension.values.length > 0);
    const visibleDimensions = filterByLabel(populatedDimensions, searchQuery);

    return (
        <Stack gap={theme.spacing(1)}>
            <FoodsTextInput
                value={searchQuery}
                onChange={setSearchQuery}
                ExtraInputProps={{
                    startAdornment: <FoodsIcon icon="search" size={2} />,
                    placeholder: labels.searchPlaceholder,
                    "aria-label": labels.searchPlaceholder,
                }}
            />
            <Typography variant="small-regular" color="text.secondary">
                {labels.counter(visibleDimensions.length, populatedDimensions.length)}
            </Typography>
            {visibleDimensions.length === 0 ?
                <Typography variant="small-regular" color="text.secondary">
                    {labels.empty}
                </Typography>
            :   <Stack>
                    {visibleDimensions.map((dimension, index) => (
                        <Stack key={dimension.id}>
                            {index > 0 && <Divider sx={{ marginY: theme.spacing(0.5) }} />}
                            <FacetDimensionRow
                                dimension={dimension}
                                labels={labels}
                                isCollapsible={isCollapsible}
                                isExpanded={!isCollapsible || !collapsedDimensionIds.has(dimension.id)}
                                onToggleExpanded={() =>
                                    setCollapsedDimensionIds((current) => toggleSetMembership(current, dimension.id))
                                }
                                showAllValues={expandedValueDimensionIds.has(dimension.id)}
                                onToggleShowAllValues={() =>
                                    setExpandedValueDimensionIds((current) =>
                                        toggleSetMembership(current, dimension.id),
                                    )
                                }
                                valueFilterQuery={valueFilterQueries[dimension.id] ?? ""}
                                onValueFilterQueryChange={(query) =>
                                    setValueFilterQueries((current) => ({ ...current, [dimension.id]: query }))
                                }
                                onSelectionChange={(selection) => onSelectionChange(dimension.id, selection)}
                            />
                        </Stack>
                    ))}
                </Stack>
            }
        </Stack>
    );
};
